import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { volApi, formatVolError } from "./volApi";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";
import { Loader2, Check } from "lucide-react";

const COUNTIES = [
  "Beaufort", "Bertie", "Camden", "Chowan", "Currituck", "Dare", "Gates",
  "Hertford", "Hyde", "Martin", "Pasquotank", "Perquimans", "Pitt",
  "Tyrrell", "Washington", "Wayne",
];

export default function VolunteerSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    counties: [], skills: "", why_volunteer: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleCounty = (c) => {
    setForm((f) => ({
      ...f,
      counties: f.counties.includes(c)
        ? f.counties.filter((x) => x !== c)
        : [...f.counties, c],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await volApi.post("/volunteers/signup", form);
      setDone(true);
    } catch (err) {
      setError(formatVolError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white border border-[#E5E5E0] rounded-2xl p-10" data-testid="volunteer-signup-success">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#7E937D] text-white flex items-center justify-center">
            <Check className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-2xl font-semibold mt-5">Application received</h2>
          <p className="text-[#334155] mt-3">
            Thank you for signing up to volunteer with HarborWell. We&apos;ll review your
            application and email you when it&apos;s approved — usually within a few business days.
          </p>
          <Link to="/volunteer/login" data-testid="volunteer-signup-go-login" className="inline-block mt-6 px-5 py-2.5 rounded-full border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#E0E7FF]">
            Back to sign-in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <HarborWellFormHeader
            eyebrow="Volunteer Portal"
            homeHref="/volunteer/login"
            size="md"
            testId="volunteer-signup-brand-header"
          />
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 sm:p-10">
          <div className="hw-eyebrow mb-2">Join the team</div>
          <h1 className="font-display text-3xl font-semibold text-[#0F172A] mb-2">Volunteer with HarborWell</h1>
          <p className="text-[#334155] mb-8">
            Tell us a bit about yourself. We&apos;ll review your application and reach out within
            a few business days.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="volunteer-signup-form">
            <Field label="Full name" required>
              <input
                type="text" required value={form.name} onChange={set("name")}
                data-testid="volunteer-signup-name"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Email" required>
                <input
                  type="email" required value={form.email} onChange={set("email")}
                  data-testid="volunteer-signup-email"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel" value={form.phone} onChange={set("phone")}
                  data-testid="volunteer-signup-phone"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
                />
              </Field>
            </div>
            <Field label="Choose a password" required hint="At least 8 characters">
              <input
                type="password" required minLength={8} value={form.password} onChange={set("password")}
                data-testid="volunteer-signup-password"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
              />
            </Field>
            <Field label="Counties you can serve (optional)" hint="Select all that apply">
              <div className="flex flex-wrap gap-2" data-testid="volunteer-signup-counties">
                {COUNTIES.map((c) => {
                  const active = form.counties.includes(c);
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => toggleCounty(c)}
                      data-testid={`volunteer-signup-county-${c.toLowerCase()}`}
                      className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                        active
                          ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                          : "bg-white text-[#334155] border-[#E5E5E0] hover:border-[#1E3A8A]"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Skills or experience (optional)">
              <textarea
                rows={3} value={form.skills} onChange={set("skills")}
                data-testid="volunteer-signup-skills"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
                placeholder="Medical training, fluent in Spanish, comfortable with childcare, etc."
              />
            </Field>
            <Field label="Why do you want to volunteer? (optional)">
              <textarea
                rows={3} value={form.why_volunteer} onChange={set("why_volunteer")}
                data-testid="volunteer-signup-why"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
              />
            </Field>

            {error && (
              <div data-testid="volunteer-signup-error" className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="volunteer-signup-submit"
              className="w-full py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit application
            </button>

            <p className="text-xs text-center text-[#1E3A8A]">
              Already have an account?{" "}
              <Link to="/volunteer/login" className="font-medium hover:underline">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, hint, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#334155] mb-2">
      {label}{required && <span className="text-[#DC2626] ml-1">*</span>}
      {hint && <span className="ml-2 text-xs text-[#1E3A8A] font-normal">{hint}</span>}
    </label>
    {children}
  </div>
);
